import React, { useEffect, useMemo, useRef } from "react";
import {
  View,
  TextInput,
  StyleSheet,
  Platform,
  Animated,
  useWindowDimensions,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setFormValue,
  setNotes,
  setValidationErrors,
} from "../../store/slices/pulseCheckSlice";
import { getMetricCardWidth } from "../../utils/getMetricCardWidth";
import { useTheme } from "../../theme";
import { ThemeColors } from "../../theme/types";
import LHSectionHeader from "../../components/LHSectionHeader";
import LhMetricCard from "../../components/LhMetricCard";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

interface PulseFirstCheckProps {}

// Note: `notesInputDisabled` keeps its exact literal colors (#DADADA /
// #B1B1B1 / #2D2D2D) because existing tests assert on those specific hex
// values. All other colors resolve from the shared theme tokens so the card
// reads correctly in both Light and Dark mode.
const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 24,
      borderRadius: 6,
      overflow: 'hidden',
      elevation: 4,
      shadowColor: 'rgba(0, 94, 192, 0.08)',
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    firstCheckHeader: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    header1text: {
      fontSize: 16,
    },
    metricCardContainer: {
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      padding: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderTopWidth: 0,
      borderColor: colors.border.default,
    },
    metricRowWrapper: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      gap: Platform.OS === "web" ? 16 : 12,
    },
    notesInput: {
      borderWidth: 1,
      borderColor: colors.input.border,
      borderRadius: 6,
      marginTop: Platform.OS === "web" ? "1%" : 30,
      padding: 12,
      minHeight: 100,
      backgroundColor: colors.input.background,
      color: colors.text.primary,
    },
    notesInputDisabled: {
      backgroundColor: "#DADADA",
      borderColor: "#B1B1B1",
      color: "#2D2D2D",
    },
  });

const PulseFirstCheck: React.FC<PulseFirstCheckProps> = () => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const formValues = useSelector(
    (state: RootState) => state.pulseCheck.formValues
  );
  const notes = useSelector((state: RootState) => state.pulseCheck.notes);
  const validationErrors = useSelector(
    (state: RootState) => state.pulseCheck.validationErrors
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );
  const { width: screenWidth } = useWindowDimensions();

  const cardWidth = React.useMemo(
    () => getMetricCardWidth(screenWidth),
    [screenWidth]
  );

  // Add animation values for error highlighting
  const errorAnimations = {
    metric1: useRef(new Animated.Value(0)).current,
    result1: useRef(new Animated.Value(0)).current,
    target1: useRef(new Animated.Value(0)).current,
  };

  // Trigger animation when validation errors change
  // requestAnimationFrame defers setValue calls outside React's commit phase,
  // preventing synchronous nested state updates on react-native-web.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      Object.keys(errorAnimations).forEach((field) => {
        if (validationErrors[field]) {
          Animated.sequence([
            Animated.timing(
              errorAnimations[field as keyof typeof errorAnimations],
              {
                toValue: 1,
                duration: 300,
                useNativeDriver: false,
              }
            ),
            Animated.timing(
              errorAnimations[field as keyof typeof errorAnimations],
              {
                toValue: 0.7,
                duration: 200,
                useNativeDriver: false,
              }
            ),
            Animated.timing(
              errorAnimations[field as keyof typeof errorAnimations],
              {
                toValue: 1,
                duration: 200,
                useNativeDriver: false,
              }
            ),
          ]).start();
        } else {
          // Reset animation if error is cleared
          errorAnimations[field as keyof typeof errorAnimations].setValue(0);
        }
      });
    });
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationErrors]);

  const handleFormChange = (field: string, value: string): void => {
    dispatch(setFormValue({ field, value }));

    // Clear validation error for this field if it was previously flagged
    if (validationErrors[field]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[field];
      dispatch(setValidationErrors(updatedErrors));

      // Play a success animation when field becomes valid
      if (value.trim() !== "") {
        // Reset the animation value first
        errorAnimations[field as keyof typeof errorAnimations]?.setValue(0);

        // Trigger a subtle success animation
        Animated.sequence([
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 0.5,
              duration: 200,
              useNativeDriver: false,
            }
          ),
          Animated.timing(
            errorAnimations[field as keyof typeof errorAnimations],
            {
              toValue: 0,
              duration: 300,
              useNativeDriver: false,
            }
          ),
        ]).start();
      }
    }
  };

  const handleNotesChange = (value: string): void => {
    dispatch(setNotes({ which: "first", value }));
  };

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title="First Check"
        containerStyle={styles.firstCheckHeader}
        textStyle={styles.header1text}
      />
      <View style={styles.metricCardContainer}>
        <View style={styles.metricRowWrapper}>
          <Animated.View
            style={{
              opacity: Animated.add(
                0.8,
                errorAnimations.metric1.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 0.2],
                })
              ),
              transform: [
                {
                  scale: errorAnimations.metric1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.02],
                  }),
                },
              ],
            }}
          >
            <LhMetricCard
              testID="metric1-card"
              headerTitle="Metric / Topic Discussed"
              inputValue={formValues.metric1}
              onInputChange={(v) => handleFormChange("metric1", v)}
              width={cardWidth}
              height={Platform.OS === "web" ? 80 : 75}
              inputHeight={Platform.OS === "web" ? 40 : 36}
              hasError={validationErrors.metric1}
              fieldId="metric1-field"
              disabled={pulseState === "completed"}
            />
          </Animated.View>

          <Animated.View
            style={{
              opacity: Animated.add(
                0.8,
                errorAnimations.result1.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 0.2],
                })
              ),
              transform: [
                {
                  scale: errorAnimations.result1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.02],
                  }),
                },
              ],
            }}
          >
            <LhMetricCard
              testID="result1-field"
              headerTitle="Last Shift Results"
              inputValue={formValues.result1}
              onInputChange={(v) => handleFormChange("result1", v)}
              width={cardWidth}
              height={Platform.OS === "web" ? 80 : 75}
              inputHeight={Platform.OS === "web" ? 40 : 36}
              hasError={validationErrors.result1}
              fieldId="result1-field"
              disabled={pulseState === "completed"}
            />
          </Animated.View>

          <Animated.View
            style={{
              opacity: Animated.add(
                0.8,
                errorAnimations.target1.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 0.2],
                })
              ),
              transform: [
                {
                  scale: errorAnimations.target1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, 1.02],
                  }),
                },
              ],
            }}
          >
            <LhMetricCard
              testID="target1-field"
              headerTitle="Target"
              inputValue={formValues.target1}
              onInputChange={(v) => handleFormChange("target1", v)}
              width={cardWidth}
              height={Platform.OS === "web" ? 80 : 75}
              inputHeight={Platform.OS === "web" ? 40 : 36}
              hasError={validationErrors.target1}
              fieldId="target1-field"
              disabled={pulseState === "completed"}
            />
          </Animated.View>
        </View>
        <TextInput
          style={[
            styles.notesInput,
            pulseState === "completed" && styles.notesInputDisabled,
          ]}
          placeholder="Notes from first conversation..."
          placeholderTextColor={colors.text.secondary}
          multiline
          value={notes.first}
          onChangeText={handleNotesChange}
          editable={pulseState !== "completed"}
        />
      </View>
    </View>
  );
};

export default PulseFirstCheck;
